import { environment, isProdEnvIndeed } from '../environment';
import { _setMonitoringRefs } from '../telemetry';
import { initDevWebVitals } from './webVitals';

const INIT_MAP = {
  sentry: false,
  datadog: false,
  logrocket: false,
  clarity: false,
};

type MonitoringUser = {
  id?: string;
  email?: string;
  name?: string;
  accountName?: string;
  accountId?: string;
  accountType?: string;
  trialStatus: string;
  trialStartDate?: string;
  trialEndDate?: string;
  crmOrgsCount: number;
};

let datadogRumRef: any = null;
let logRocketRef: any = null;

let pendingUser: MonitoringUser | null = null;

let sdksReady = false;

const initDataDog = (datadogRum: any) => {
  datadogRum.init({
    applicationId: '1d06024f-c19e-4726-8564-a8a92e5edb52',
    clientToken: 'pubb3cb1e056c1dea96db4341541fd87d9d',
    site: 'datadoghq.com',
    proxy: (options: any) => `https://ddrum.sweep.io${options.path}/?${options.parameters}`,
    service: 'sweep.io',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [import.meta.env.VITE_SERVER_URL],
  });
  datadogRum.startSessionReplayRecording();
  INIT_MAP.datadog = true;
};

const initSentry = (Sentry: any) => {
  Sentry.init({
    dsn: 'https://d72ec60a869348d48d3b745c8f6f7bfd@o1189061.ingest.sentry.io/6309317',
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    environment: environment,
  });
  INIT_MAP.sentry = true;
};

const initLogRocket = (LogRocket: any, Sentry: any) => {
  LogRocket.init('7759z2/sweep');
  INIT_MAP.logrocket = true;
  LogRocket.getSessionURL((sessionURL: string) => {
    if (INIT_MAP.sentry) {
      Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
    }
  });
};

const initClarity = () => {
  if ((window as any).clarity) {
    INIT_MAP.clarity = true;
  }
};

const setUserInDataDog = (user: MonitoringUser) => {
  datadogRumRef?.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
    accountName: user.accountName,
    accountId: user.accountId,
    plan: user.accountType,
    trialStartDate: user.trialStartDate,
    trialEndDate: user.trialEndDate,
    trialStatus: user.trialStatus,
    crmOrgsCount: user.crmOrgsCount,
  });
};

const setUserInLogRocket = (user: MonitoringUser) => {
  logRocketRef?.identify(user.id ?? '', {
    name: user.name ?? 'unknown',
    email: user.email ?? 'unknown',
    accountId: user.accountId ?? 'unknown',
    accountName: user.accountName ?? 'unknown',
  });
};

const setUserInClarity = (user: MonitoringUser) => {
  (window as any).clarity('set', 'email', user.email ?? 'unknown');
  (window as any).clarity('set', 'accountId', user.accountId ?? 'unknown');
  (window as any).clarity('set', 'accountName', user.accountName ?? 'unknown');
};

const applyUserDetails = (user: MonitoringUser) => {
  if (INIT_MAP.datadog) {
    setUserInDataDog(user);
  }
  if (INIT_MAP.logrocket) {
    setUserInLogRocket(user);
  }
  if (INIT_MAP.clarity) {
    setUserInClarity(user);
  }
};

export const initMonitoring = async (force = false) => {
  if (import.meta.env.DEV) {
    initDevWebVitals();
  }

  if (!(isProdEnvIndeed() || force)) {
    return;
  }

  const [{ datadogRum }, Sentry, LogRocket] = await Promise.all([
    import('@datadog/browser-rum'),
    import('@sentry/react'),
    import('logrocket').then((module) => module.default),
  ]);

  datadogRumRef = datadogRum;
  logRocketRef = LogRocket;

  initDataDog(datadogRum);
  initSentry(Sentry);
  initLogRocket(LogRocket, Sentry);
  initClarity();

  _setMonitoringRefs(Sentry, datadogRum);

  sdksReady = true;

  if (pendingUser) {
    applyUserDetails(pendingUser);
    pendingUser = null;
  }
};

export const setUserDetailsForMonitoring = (user: MonitoringUser) => {
  if (!sdksReady) {
    pendingUser = user;
    return;
  }
  applyUserDetails(user);
};
